/**
 * 🔌 useRealTime - Hook pour écouter les événements temps réel WebSocket
 * 
 * Usage:
 * const { profileUpdates, newMatches, guideMessages } = useRealTime();
 * 
 * Ou avec callbacks personnalisés:
 * useRealTime({
 *   onProfileUpdate: (data) => console.log('Profile updated:', data),
 *   onNewMatch: (data) => showConfetti(),
 *   onGuideMessage: (data) => showGuidePopup(data)
 * });
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';

export function useRealTime(options = {}) {
  const {
    onProfileUpdate,
    onNewMatch,
    onGuideMessage,
    onSwipeReceived,
    onNotification,
    showToasts = true,  // Afficher des toasts automatiques pour certains événements
  } = options;

  const [profileUpdates, setProfileUpdates] = useState([]);
  const [newMatches, setNewMatches] = useState([]);
  const [guideMessages, setGuideMessages] = useState([]);
  const [swipesReceived, setSwipesReceived] = useState([]);
  const [notifications, setNotifications] = useState([]);
  
  // Ref pour éviter les re-renders inutiles
  const callbacksRef = useRef({ onProfileUpdate, onNewMatch, onGuideMessage, onSwipeReceived, onNotification });
  callbacksRef.current = { onProfileUpdate, onNewMatch, onGuideMessage, onSwipeReceived, onNotification };

  // Écouter les événements WebSocket via CustomEvents
  useEffect(() => {
    const handleProfileUpdate = (event) => {
      const data = event.detail;
      setProfileUpdates(prev => [...prev.slice(-9), data]); // Garder max 10
      callbacksRef.current.onProfileUpdate?.(data);
    };

    const handleNewMatch = (event) => {
      const data = event.detail;
      setNewMatches(prev => [...prev.slice(-9), data]);
      callbacksRef.current.onNewMatch?.(data);
      
      // Toast automatique pour nouveau match
      if (showToasts) {
        toast.success(`💕 Nouveau match avec ${data.partnerName || 'quelqu\'un'} !`, {
          description: 'Envoie-lui un message !',
          duration: 5000,
        });
      }
    };

    const handleGuideMessage = (event) => {
      const data = event.detail;
      setGuideMessages(prev => [...prev.slice(-9), data]);
      callbacksRef.current.onGuideMessage?.(data);
      
      // Toast automatique pour message de guide
      if (showToasts) {
        toast.info(`${data.guideEmoji || '🌙'} ${data.guideName || 'Guide'} a un message pour toi`, {
          description: data.message?.substring(0, 50) + '...',
          duration: 4000,
        });
      }
    };

    const handleSwipeReceived = (event) => {
      const data = event.detail;
      setSwipesReceived(prev => [...prev.slice(-19), data]); // Garder max 20
      callbacksRef.current.onSwipeReceived?.(data);
      
      // Toast pour super-like seulement
      if (showToasts && data.swipeType === 'super_like') {
        toast.success('⭐ Quelqu\'un t\'a envoyé un Super Like !', {
          duration: 4000,
        });
      }
    };

    const handleNotification = (event) => {
      const data = event.detail;
      setNotifications(prev => [...prev.slice(-19), data]);
      callbacksRef.current.onNotification?.(data);
    };

    // Écouter les événements
    window.addEventListener('ws:profile_updated', handleProfileUpdate);
    window.addEventListener('ws:new_match', handleNewMatch);
    window.addEventListener('ws:guide_message', handleGuideMessage);
    window.addEventListener('ws:swipe_received', handleSwipeReceived);
    window.addEventListener('ws:notification', handleNotification);

    // Cleanup
    return () => {
      window.removeEventListener('ws:profile_updated', handleProfileUpdate);
      window.removeEventListener('ws:new_match', handleNewMatch);
      window.removeEventListener('ws:guide_message', handleGuideMessage);
      window.removeEventListener('ws:swipe_received', handleSwipeReceived);
      window.removeEventListener('ws:notification', handleNotification);
    };
  }, [showToasts]);

  // Fonction pour effacer les événements
  const clearProfileUpdates = useCallback(() => setProfileUpdates([]), []);
  const clearNewMatches = useCallback(() => setNewMatches([]), []);
  const clearGuideMessages = useCallback(() => setGuideMessages([]), []);
  const clearSwipesReceived = useCallback(() => setSwipesReceived([]), []);
  const clearNotifications = useCallback(() => setNotifications([]), []);
  const clearAll = useCallback(() => {
    setProfileUpdates([]);
    setNewMatches([]);
    setGuideMessages([]);
    setSwipesReceived([]);
    setNotifications([]);
  }, []);

  return {
    // Données
    profileUpdates,
    newMatches,
    guideMessages,
    swipesReceived,
    notifications,
    
    // Compteurs
    unreadMatchesCount: newMatches.length,
    unreadGuideMessagesCount: guideMessages.length,
    unreadSwipesCount: swipesReceived.length,
    
    // Actions
    clearProfileUpdates,
    clearNewMatches,
    clearGuideMessages,
    clearSwipesReceived,
    clearNotifications,
    clearAll,
  };
}

/**
 * 🎯 Hook simplifié pour un seul événement
 */
export function useRealTimeEvent(eventName, callback) {
  useEffect(() => {
    if (!callback) return;
    
    const handler = (event) => callback(event.detail);
    window.addEventListener(`ws:${eventName}`, handler);
    
    return () => window.removeEventListener(`ws:${eventName}`, handler);
  }, [eventName, callback]);
}

export default useRealTime;
